import { useCallback, useEffect, useRef, useState } from 'react';
import { apiUrl } from '../../config/api';

/**
 * Paginated product search.
 *
 * `filters` is serialised into the dependency so callers can pass an inline
 * object without re-fetching on every render, and a superseded request is
 * aborted so a slow earlier response cannot overwrite a newer page.
 */
const useProducts = (filters = {}, { auto = true } = {}) => {
  const [products, setProducts] = useState([]);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filterKey = JSON.stringify(filters);
  const abortRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const fetchProducts = useCallback(
    async (pageNumber = 1) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const response = await fetch(
          apiUrl(`/api/products/fetch?page=${pageNumber}`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: filterKey,
            signal: controller.signal,
          }
        );
        if (!response.ok) throw new Error('Network response was not ok');

        const data = await response.json();
        if (!isMounted.current) return;

        setProducts(data.data || []);
        setLastPage(data.last_page || 1);
        setCurrentPage(data.current_page || 1);
        setError(null);
      } catch (err) {
        if (err.name === 'AbortError' || !isMounted.current) return;
        setError('We could not load these products.');
        setProducts([]);
      } finally {
        if (isMounted.current && !controller.signal.aborted) setLoading(false);
      }
    },
    [filterKey]
  );

  useEffect(() => {
    if (auto) fetchProducts(1);
  }, [auto, fetchProducts]);

  return {
    products,
    currentPage,
    lastPage,
    loading,
    error,
    fetchProducts,
  };
};

export default useProducts;
